/**
 * Retry a function with exponential backoff
 * Delays: 2s, 4s, 8s between retries
 */

export interface RetryOptions {
  maxRetries?: number;
  onRetry?: (attempt: number, error: any) => void;
}

export interface RetryResult<T> {
  result: T;
  attempts: number;
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<RetryResult<T>> {
  const { maxRetries = 3, onRetry } = options;
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const result = await fn();
      return { result, attempts: attempt };
    } catch (error) {
      lastError = error;
      
      // Don't retry on last attempt
      if (attempt === maxRetries) {
        break;
      }
      
      // Call onRetry callback if provided
      if (onRetry) {
        onRetry(attempt, error);
      }
      
      // Exponential backoff: 2s, 4s, 8s
      const delayMs = Math.pow(2, attempt) * 1000;
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  throw lastError;
}
